// Nombre Archivo: component-scrolling-text.js
// Ubicación: /assets/component-scrolling-text.js

class ScrollingText extends HTMLElement {
  constructor() {
    super(); // Llama al constructor de HTMLElement

    this.scrollingContainer = this.querySelector('.scrolling-text__container');
    if (!this.scrollingContainer) {
      console.warn('ScrollingText: .scrolling-text__container not found on init.');
      return;
    }

    this.pauseOnHover = this.getAttribute('data-pause-on-hover') === 'true';
    this.direction = this.getAttribute('data-scrolling-direction') === 'rtl' ? 'rtl' : 'ltr';
    this.speed = parseInt(this.getAttribute('data-scrolling-speed'), 10) || 100; // Default 100

    // Variables de estado
    this.animationId = null;
    this.startTime = null;
    this.containerWidth = 0;
    this.contentWidth = 0;
    this.originalContentWidth = 0;
    this.position = 0;
    this.pausedTime = 0;
    this.isPaused = false;

    this.originalItems = Array.from(this.scrollingContainer.children);

    // Bind 'this'
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.animateScroll = this.animateScroll.bind(this);
    this.handleResize = debounce(this.handleResize.bind(this), 250);
  }

  connectedCallback() {
    if (!this.scrollingContainer || this.originalItems.length === 0) {
      console.warn('ScrollingText: No initial items found or container missing.');
      return;
    }
    requestAnimationFrame(() => {
      this.setupScrollingContent();
      this.initScrolling();
      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', this.handleMouseEnter);
        this.addEventListener('mouseleave', this.handleMouseLeave);
      }
      window.addEventListener('resize', this.handleResize);
    });
  }

  disconnectedCallback() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    if (this.pauseOnHover) {
      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
    }
    window.removeEventListener('resize', this.handleResize);
  }

  setupScrollingContent() {
    while (this.scrollingContainer.firstChild) {
      this.scrollingContainer.removeChild(this.scrollingContainer.firstChild);
    }
    this.originalItems.forEach(item => {
      this.scrollingContainer.appendChild(item.cloneNode(true));
    });

    this.updateWidths(true); // Medir originales

    if (this.containerWidth <= 0 || this.originalContentWidth <= 0) {
      console.warn('ScrollingText: Container or content width is zero, cannot setup scroll.');
      return;
    }

    // +2 para asegurar un margen amplio, especialmente en pantallas anchas
    const neededCopies = Math.ceil((this.containerWidth * 2) / this.originalContentWidth) + 2;

    for (let i = 0; i < neededCopies; i++) {
      this.originalItems.forEach(item => {
        this.scrollingContainer.appendChild(item.cloneNode(true));
      });
    }
    this.updateWidths(false); // Medir todo
  }

  updateWidths(measureOriginalsOnly = false) {
    this.containerWidth = this.offsetWidth;
    const itemsToMeasure = measureOriginalsOnly ?
                           this.originalItems.map(original => this.scrollingContainer.querySelector(`:scope > *:nth-child(${this.originalItems.indexOf(original) + 1})`)).filter(Boolean)
                           : Array.from(this.scrollingContainer.children);

    if (!itemsToMeasure || itemsToMeasure.length === 0) {
        if (measureOriginalsOnly) this.originalContentWidth = 0;
        else this.contentWidth = 0;
        return;
    }

    const totalWidth = itemsToMeasure.reduce((total, item) => {
        const style = window.getComputedStyle(item);
        const marginLeft = parseFloat(style.marginLeft) || 0;
        const marginRight = parseFloat(style.marginRight) || 0;
        return total + item.offsetWidth + marginLeft + marginRight;
    }, 0);

    if (measureOriginalsOnly) {
        this.originalContentWidth = Math.max(totalWidth, 1);
    } else {
        this.contentWidth = Math.max(totalWidth, 1);
    }
  }

  // -------- ¡FUNCIÓN CORREGIDA! --------
  getPixelsPerSecond() {
    // Asegurarse de que speed no sea 0 para evitar división por cero
    const safeSpeed = Math.max(this.speed, 1);
    // Factor de ajuste para escalar la velocidad a un rango más útil.
    // Puedes experimentar con este valor si la velocidad general sigue mal.
    // Valores más altos aquí harán que todo sea más rápido.
    const speedFactor = 10; // ¡AJUSTADO!

    if (this.originalContentWidth <= 0) return 50; // Velocidad de fallback

    // Nueva fórmula: (ancho / valor_slider) * factor_ajuste
    // Mayor valor_slider (más lento) -> menor px/seg
    // Mayor ancho -> mayor px/seg
    return (this.originalContentWidth / safeSpeed) * speedFactor;
  }
  // -------- FIN FUNCIÓN CORREGIDA --------

  initScrolling() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    if(this.originalContentWidth <= 0) {
        console.warn("ScrollingText: Cannot start animation with zero content width.");
        return;
    }
    this.position = 0;
    this.startTime = performance.now();
    this.isPaused = false;
    this.animateScroll();
  }

  animateScroll() {
    if (this.isPaused) return;

    const currentTime = performance.now();
    const elapsedTime = currentTime - this.startTime;
    const pixelsPerSecond = this.getPixelsPerSecond();

    this.position = (elapsedTime / 1000) * pixelsPerSecond;

    if (this.position >= this.originalContentWidth) {
      const overflow = this.position % this.originalContentWidth;
      // Evitar división por cero si pixelsPerSecond es 0
      const timeForOverflow = pixelsPerSecond > 0 ? (overflow * 1000 / pixelsPerSecond) : 0;
      this.startTime = currentTime - timeForOverflow;
      this.position = overflow;
    }

    const translateValue = this.direction === 'rtl' ? this.position : -this.position;
    // Asegurarse que el valor es un número finito
    if (isFinite(translateValue)) {
        this.scrollingContainer.style.transform = `translateX(${translateValue}px)`;
        this.scrollingContainer.style.willChange = 'transform';
    } else {
        console.warn("ScrollingText: calculated translateValue is not finite", translateValue);
        // Detener animación si el cálculo falla
        if (this.animationId) cancelAnimationFrame(this.animationId);
        return;
    }


    this.animationId = requestAnimationFrame(this.animateScroll);
  }

  handleMouseEnter() {
    if (!this.isPaused && this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
      this.pausedTime = performance.now();
      this.isPaused = true;
    }
  }

  handleMouseLeave() {
    if (this.isPaused) {
      const pauseDuration = performance.now() - this.pausedTime;
      this.startTime += pauseDuration;
      this.isPaused = false;
      this.animateScroll();
    }
  }

  handleResize() {
    if (this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
    }
    this.setupScrollingContent();
    this.initScrolling();
  }
}

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func.apply(this, args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

if (!customElements.get('scrolling-text')) {
  customElements.define('scrolling-text', ScrollingText);
}